import { Check, Plus, Search, X } from 'lucide-react'
import { useMemo, useState, type KeyboardEvent } from 'react'

import type { ProfileSkillView } from '@/features/profile/types'

/**
 * `suggestions` is the catalog to search — `bs-core.skills` via `getSkills()`, mapped to
 * `{skillId: _id, skillName: value}`. Free-solo creation (`skillId: null`) stays available — a
 * skill missing from the catalog is a real, common case.
 */
export function ProfileSkillsEditor({ value, onChange, suggestions }: { value: ProfileSkillView[]; onChange(value: ProfileSkillView[]): void; suggestions: ProfileSkillView[] }) {
  const [query, setQuery] = useState('')
  const selectedNames = new Set(value.map((skill) => skill.skillName.toLowerCase()))
  const filtered = useMemo(
    () => suggestions.filter((skill) => skill.skillName.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 50),
    [suggestions, query],
  )
  const canCreate = Boolean(query.trim()) && !selectedNames.has(query.trim().toLowerCase()) && !suggestions.some((skill) => skill.skillName.toLowerCase() === query.trim().toLowerCase())

  function add(skill: ProfileSkillView) {
    if (selectedNames.has(skill.skillName.toLowerCase())) return
    onChange([...value, skill])
    setQuery('')
  }

  function createFreeSolo() {
    const skillName = query.trim()
    if (!skillName || selectedNames.has(skillName.toLowerCase())) return
    onChange([...value, { skillId: null, skillName }])
    setQuery('')
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter' && canCreate) {
      event.preventDefault()
      createFreeSolo()
    }
  }

  return (
    <div>
      <p className="mb-2 text-sm font-semibold text-[#555]">Profile skills</p>
      <p className="mb-3 text-xs leading-5 text-muted-foreground">Describe skills you actually have. Select a suggestion or type a custom skill and press Enter.</p>
      <div className="rounded-xl border border-input-border bg-input p-3 transition duration-200 focus-within:border-accent">
        {value.length ? <div className="mb-3 flex flex-wrap gap-2">{value.map((skill) => <button key={`${skill.skillId ?? 'custom'}-${skill.skillName}`} type="button" onClick={() => onChange(value.filter((item) => item.skillName !== skill.skillName))} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${skill.skillId ? 'bg-primary text-white' : 'bg-[#eef0f7] text-primary'}`}>{skill.skillName}{!skill.skillId ? <span className="text-[10px] opacity-65">custom</span> : null}<X className="size-3" /></button>)}</div> : null}
        <div className="relative">
          <Search className="absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={onKeyDown} placeholder="Select..." className="h-9 w-full bg-transparent pl-8 pr-2 text-base outline-none placeholder:text-[#c7c7c7] focus:placeholder:text-transparent" />
        </div>
        <div className="mt-2 max-h-48 overflow-y-auto border-t border-border pt-2">
          {canCreate ? <button type="button" onClick={createFreeSolo} className="mb-1 flex w-full items-center gap-2 rounded-lg bg-accent/[0.06] px-3 py-2 text-left text-sm font-semibold text-primary"><Plus className="size-4 text-accent" />Create “{query.trim()}”</button> : null}
          {filtered.map((skill) => {
            const selected = selectedNames.has(skill.skillName.toLowerCase())
            return <button key={skill.skillId} type="button" onClick={() => selected ? onChange(value.filter((item) => item.skillName.toLowerCase() !== skill.skillName.toLowerCase())) : add(skill)} className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition ${selected ? 'bg-accent/[0.08] font-semibold text-primary' : 'text-[#555] hover:bg-muted'}`}><span>{skill.skillName}</span>{selected ? <Check className="size-4 text-accent" /> : null}</button>
          })}
        </div>
      </div>
    </div>
  )
}
